import { Table, TradeButton } from '@/components';
import {
  TableCellProps,
  TableRowProps,
} from '@/components/molecules/Table/interfaces/Table';
import { useMarketData } from '@/components/providers';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import { MoralisMarketDataTokenInterface } from '@/interfaces';
import {
  formatNumberToAmountInUsdString,
  formatPercentage,
} from '@/utils/formatUtils';
import { FC, useMemo, useState } from 'react';
import { FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6';
interface Props {
  limit?: number;
  showAllDetails?: boolean;
  showSomeDetails?: boolean;
  rightAlignLastColumn?: boolean;
}
const TrendUpIcon = FaArrowTrendUp as React.ComponentType<any>;
const TrendDownIcon = FaArrowTrendDown as React.ComponentType<any>;
const MarketTopAssetsList: FC<Props> = ({
  limit = 100,
  showAllDetails = true,
  showSomeDetails = false,
  rightAlignLastColumn = false,
}) => {
  const { globalMarketData, isLoading, error } = useMarketData();
  const { getTokens, setAssetToBuy } = useTradeableTokens();
  const [sortedColumn, setSortedColumn] = useState<number>(1);
  const [isAscending, setIsAscending] = useState<boolean>(false);
  const handleSort = (columnIndex: number) => {
    if (sortedColumn === columnIndex) {
      setIsAscending(!isAscending);
    } else {
      setSortedColumn(columnIndex);
      setIsAscending(true);
    }
  };
  const getComparableValue = (
    token: MoralisMarketDataTokenInterface,
    columnIndex: number,
  ): string | number => {
    switch (columnIndex) {
      case 0:
        return token.name || '';
      case 1:
        return parseFloat(token.market_cap_usd || '0');
      case 2:
        return parseFloat(token.usd_price || '0');
      case 3:
        return parseFloat(token.total_volume || '0');
      case 4:
        return parseFloat(token.ath_percent_change || '0');
      default:
        return '';
    }
  };
  const sortedData = useMemo(() => {
    if (sortedColumn === -1) return globalMarketData.slice(0, limit);
    return [...globalMarketData]
      .sort((a, b) => {
        const aValue = getComparableValue(a, sortedColumn);
        const bValue = getComparableValue(b, sortedColumn);
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return isAscending
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return isAscending
            ? Number(aValue) - Number(bValue)
            : Number(bValue) - Number(aValue);
        }
      })
      .slice(0, limit);
  }, [globalMarketData, sortedColumn, isAscending, limit]);
  const rows = useMemo(() => {
    const tradeableTokens = getTokens();
    const tradeableSymbols = new Set(
      tradeableTokens.map((token) => token.symbol.toLowerCase()),
    );
    return sortedData.map((token: MoralisMarketDataTokenInterface) => {
      const cells: TableCellProps[] = [
        {
          mainText: token.symbol.toUpperCase(),
          subText: token.name,
          leftIconUrl: token.logo || '',
          leftIconBorderRadius: '50%',
        },
        {
          mainText: formatNumberToAmountInUsdString(
            parseFloat(token.market_cap_usd || '0'),
          ),
          subText: formatPercentage(
            parseFloat(token.market_cap_24hr_percent_change || '0'),
          ),
          rightIcon:
            parseFloat(token.market_cap_24hr_percent_change || '0') > 0 ? (
              <TrendUpIcon className="text-accent" />
            ) : (
              <TrendDownIcon className="text-error" />
            ),
          shouldMiddleAlignIconAndText: true,
          align: !showAllDetails && !showSomeDetails ? 'right' : 'left',
        },
      ];
      if (showAllDetails || showSomeDetails) {
        cells.push({
          mainText: formatNumberToAmountInUsdString(
            parseFloat(token.usd_price || '0'),
          ),
          subText: formatPercentage(
            parseFloat(token.usd_price_24hr_percent_change || '0'),
          ),
          rightIcon:
            parseFloat(token.usd_price_24hr_percent_change || '0') > 0 ? (
              <TrendUpIcon className="text-accent" />
            ) : (
              <TrendDownIcon className="text-error" />
            ),
          shouldMiddleAlignIconAndText: true,
        });
      }
      const isTokenTradeable = tradeableSymbols.has(
        token.symbol.trim().toLowerCase(),
      );
      if (showSomeDetails) {
        cells.push({
          mainText: formatNumberToAmountInUsdString(
            parseFloat(token.total_volume || '0'),
          ),
          shouldMiddleAlignIconAndText: true,
        });
      }
      if (showAllDetails) {
        cells.push(
          {
            mainText: formatNumberToAmountInUsdString(
              parseFloat(token.total_volume || '0'),
            ),
            shouldMiddleAlignIconAndText: true,
          },
          {
            mainText: formatNumberToAmountInUsdString(
              parseFloat(token.usd_price_ath || '0'),
            ),
            subText: formatPercentage(
              parseFloat(token.ath_percent_change || '0'),
            ),
            rightIcon:
              parseFloat(token.ath_percent_change || '0') > 0 ? (
                <TrendUpIcon className="text-accent" />
              ) : (
                <TrendDownIcon className="text-error" />
              ),
            shouldMiddleAlignIconAndText: true,
          },
          {
            mainText: isTokenTradeable ? (
              <TradeButton
                intent="ghost"
                onClick={() => {
                  const selectedToken =
                    tradeableTokens.find(
                      (t) =>
                        t.symbol.toLowerCase() === token.symbol.toLowerCase(),
                    ) ?? null;
                  setAssetToBuy(selectedToken);
                }}
              />
            ) : (
              <TradeButton intent="ghost" disabled>
                Trade
              </TradeButton>
            ),
          },
        );
      }
      return { cells };
    });
  }, [sortedData, showAllDetails, showSomeDetails, getTokens, setAssetToBuy]);
  const columnHeaders = showAllDetails
    ? [
        'Asset',
        'Market Cap.',
        'Price',
        'Daily Volume',
        'All Time High',
        'Trade',
      ]
    : showSomeDetails
    ? ['Asset', 'Market Cap.', 'Price', 'Daily Volume']
    : ['Asset', { value: 'Market Cap.', align: 'right' as 'right' }];
  const columnWidths = showAllDetails
    ? [30, 15, 15, 15, 15, 15]
    : showSomeDetails
    ? [30, 30, 25, 15]
    : [65, 35];
  const sortable = showAllDetails
    ? [true, true, true, true, true, false]
    : showSomeDetails
    ? [true, true, true, true]
    : [true, true];
  return (
    <Table
      rightAlignLastColumn={
        !(showAllDetails || showSomeDetails) || rightAlignLastColumn
      }
      columnHeaders={columnHeaders}
      columnWidths={columnWidths}
      rows={rows as TableRowProps[]}
      sortable={sortable}
      onSort={handleSort}
      sortedColumn={sortedColumn}
      isAscending={isAscending}
    />
  );
};
export default MarketTopAssetsList;
